import { useState, useContext, useEffect, useMemo, useCallback, useRef } from 'react';
import { PlusOutlined, CaretDownOutlined, PictureOutlined, VideoCameraOutlined, MoreOutlined } from '@ant-design/icons';
import { Button, Tabs, Tree, Spin, Dropdown } from 'antd';
import { MaterialManageContext } from '../hooks/MaterialManageContext';
import TreeContextMenu from './TreeContextMenu';
import FolderNameModal from '../components/FolderNameModal';
import DeleteFolderModal from '../components/DeleteFolderModal';
import MoveFolderModal from '../components/MoveFolderModal';
import Iconfont from '@utils/Iconfont';
import styles from './index.module.scss';

/**
 * 将接口返回的文件夹树数据转换为 Tree 组件所需的格式
 * 接口数据结构: { id, name, depth, imageCount, children }
 * Tree 需要: { key, title, count, isLeaf, children }
 */
const transformTreeData = (list) => {
    if (!Array.isArray(list)) return [];
    return list.map((item) => {
        const node = {
            key: item.id,
            title: item.name,
            count: item.imageCount ?? 0,
            depth: item.depth ?? 0,
            canManage: item.canManage,
            isLeaf: !item.children || item.children.length === 0,
        };
        if (item.children && item.children.length > 0) {
            node.children = transformTreeData(item.children);
        }
        return node;
    });
};
const MAX_DEPTH = 5;

const getNodeKey = (node) => node?.key ?? node?.id;
const getNodeTitle = (node) => node?.title ?? node?.name ?? '';

const findParentNode = (list, targetId, parent = null) => {
    if (!Array.isArray(list)) return null;
    for (const node of list) {
        if (String(getNodeKey(node)) === String(targetId)) return parent;
        if (node.children && node.children.length > 0) {
            const result = findParentNode(node.children, targetId, node);
            if (result !== null) return result;
        }
    }
    return null;
};

const Sidebar = () => {
    const {
        activeMenu, setActiveMenu, setActiveMenuName,
        activeTab, setActiveTab,
        menuList, menuTotal, menuLoading,
        fetchFolderTree, fetchMaterialList,
        searchKeyword,
        expandedKeys, setExpandedKeys,
        setBatchMode, setSelectedList,
    } = useContext(MaterialManageContext);

    const handleTabChange = (key) => {
        setBatchMode(false);
        setSelectedList([]);
        setActiveTab(key);
    };

    // 新建/重命名弹窗状态
    const [folderModalOpen, setFolderModalOpen] = useState(false);
    const [folderModalMode, setFolderModalMode] = useState('create');
    const [folderModalParentId, setFolderModalParentId] = useState('');
    const [folderModalRenameId, setFolderModalRenameId] = useState('');
    const [folderModalInitialName, setFolderModalInitialName] = useState('');

    // 删除弹窗状态
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteNodeId, setDeleteNodeId] = useState('');
    const [deleteFallbackTarget, setDeleteFallbackTarget] = useState({ key: 'refImage_all', name: '全部参考图' });

    // 移动弹窗状态
    const [moveModalOpen, setMoveModalOpen] = useState(false);
    const [moveNodeId, setMoveNodeId] = useState('');

    // 右键菜单状态
    const [contextMenu, setContextMenu] = useState({ visible: false, position: null, nodeData: null });
    const [hoveredNodeKey, setHoveredNodeKey] = useState(null);
    const [actionMenuOpenKey, setActionMenuOpenKey] = useState(null);

    const handleContextMenu = useCallback((e, nodeData) => {
        e.preventDefault();
        e.stopPropagation();
        setActiveMenu(nodeData.key);
        setActiveMenuName(nodeData.title);
        setActionMenuOpenKey(null);
        setContextMenu({
            visible: true,
            position: { x: e.clientX, y: e.clientY },
            nodeData,
        });
    }, [setActiveMenu, setActiveMenuName]);

    const handleContextMenuClose = useCallback(() => {
        setContextMenu({ visible: false, position: null, nodeData: null });
    }, []);

    const handleContextMenuAction = useCallback((actionKey, nodeData) => {
        switch (actionKey) {
            case 'createSub':
                setFolderModalMode('create');
                setFolderModalParentId(nodeData.key);
                setFolderModalInitialName('');
                setFolderModalOpen(true);
                break;
            case 'move':
                setMoveNodeId(nodeData.key);
                setMoveModalOpen(true);
                break;
            case 'rename':
                setFolderModalMode('rename');
                setFolderModalRenameId(nodeData.key);
                setFolderModalInitialName(nodeData.title || '');
                setFolderModalOpen(true);
                break;
            case 'delete':
                setDeleteNodeId(nodeData.key);
                {
                    const parentNode = findParentNode(menuList, nodeData.key);
                    setDeleteFallbackTarget(parentNode ? {
                        key: getNodeKey(parentNode),
                        name: getNodeTitle(parentNode),
                    } : {
                        key: 'refImage_all',
                        name: '全部参考图',
                    });
                }
                setDeleteModalOpen(true);
                break;
            default:
                break;
        }
    }, [menuList]);

    // 将接口数据转换为 Tree 组件格式
    const treeData = useMemo(() => transformTreeData(menuList), [menuList]);

    // 默认选中"全部参考图"
    useEffect(() => {
        if (!activeMenu) {
            setActiveMenu('refImage_all');
            setActiveMenuName('全部参考图');
        }
    }, [activeMenu, setActiveMenu, setActiveMenuName]);

    // 切换 tab 时重置选中状态，并默认选中"全部参考图"
    const isFirstMount = useRef(true);
    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }
        setActiveMenu('refImage_all');
        setActiveMenuName('全部参考图');
        setExpandedKeys([]);
    }, [activeTab]);

    const handleExpand = (newExpandedKeys) => {
        setExpandedKeys(newExpandedKeys);
    };

    const handleSelect = (selectedKeys, info) => {
        if (selectedKeys.length > 0) {
            const node = info.node;
            if (!node.disabled) {
                setActiveMenu(node.key);
                setActiveMenuName(node.title);
            }
        }
    };

    const handleNodeToggle = (nodeData, e) => {
        e.stopPropagation();
        if (nodeData.disabled) return;
        const isExpanded = expandedKeys.includes(nodeData.key);
        const newKeys = isExpanded
            ? expandedKeys.filter(k => k !== nodeData.key)
            : [...expandedKeys, nodeData.key];
        handleExpand(newKeys);
    };

    const getNodeMenuItems = useCallback((nodeData) => {
        if (!nodeData) return [];
        const allItems = [
            { key: 'createSub', label: '新建子文件夹', icon: <Iconfont type="icon-Material-NewFolderOutlined" /> },
            { key: 'move', label: '移动', icon: <Iconfont type="icon-Material-FolderMoveOutlined" /> },
            { key: 'rename', label: '重命名', icon: <Iconfont type="icon-Material-EditOutlined" /> },
            { key: 'delete', label: '删除', icon: <Iconfont type="icon-Material-DeleteOutlined" />, danger: true },
        ];
        let items = allItems;
        if (nodeData.depth >= MAX_DEPTH) {
            items = items.filter(item => item.key !== 'createSub');
        }
        if (nodeData.canManage === false) {
            const disableKeys = ['move', 'rename', 'delete'];
            items = items.filter(item => !disableKeys.includes(item.key));
        }
        return items.map((item) => ({
            key: item.key,
            danger: item.danger,
            label: (
                <span className={styles.menuItemLabel}>
                    <span className={styles.menuItemIcon}>{item.icon}</span>
                    <span>{item.label}</span>
                </span>
            ),
        }));
    }, []);

    // 渲染 Tree 节点
    const titleRender = (nodeData) => {
        const isActive = activeMenu === nodeData.key;
        const isDisabled = nodeData.disabled;
        const isExpanded = expandedKeys.includes(nodeData.key);
        const hasChildren = nodeData.children && nodeData.children.length > 0;
        const menuItems = getNodeMenuItems(nodeData);
        const showActionBtn =
            !isDisabled &&
            menuItems.length > 0 &&
            (hoveredNodeKey === nodeData.key || actionMenuOpenKey === nodeData.key);

        let icon = isActive || (hasChildren && isExpanded)
            ? <Iconfont type="icon-Material-FolderOpenOutlined" />
            : <Iconfont type="icon-Material-FolderOutlined" />;

        return (
            <div style={{ display: 'flex', width: '100%', overflow: 'hidden', minWidth: 0 }} onContextMenu={(e) => handleContextMenu(e, nodeData)}>
                <div
                    className={`${styles.treeNodeContent} ${isActive ? styles.activeNode : ''} ${isDisabled ? styles.disabledNode : ''}`}
                    style={{ flex: 1, minWidth: 0 }}
                    onMouseEnter={() => setHoveredNodeKey(nodeData.key)}
                    onMouseLeave={() => setHoveredNodeKey(prev => (prev === nodeData.key ? null : prev))}
                >
                    {hasChildren ? (
                        <span
                            className={`${styles.customSwitcher} ${isExpanded ? styles.expanded : ''}`}
                            onClick={(e) => handleNodeToggle(nodeData, e)}
                        >
                            <CaretDownOutlined />
                        </span>
                    ) : (
                        <span className={styles.switcherPlaceholder} />
                    )}
                    <span className={styles.nodeIcon}>{icon}</span>
                    <span className={styles.nodeTitle}>{nodeData.title}</span>
                    {nodeData.tag ? (
                        <span className={styles.nodeTag}>{nodeData.tag}</span>
                    ) : (
                        showActionBtn ? (
                            <Dropdown
                                trigger={['hover']}
                                placement="bottomRight"
                                overlayClassName={styles.nodeActionMenu}
                                menu={{
                                    items: menuItems,
                                    onClick: ({ key, domEvent }) => {
                                        domEvent?.stopPropagation();
                                        setActiveMenu(nodeData.key);
                                        setActiveMenuName(nodeData.title);
                                        handleContextMenuAction(key, nodeData);
                                        setActionMenuOpenKey(null);
                                    },
                                }}
                                open={actionMenuOpenKey === nodeData.key}
                                onOpenChange={(open) => setActionMenuOpenKey(open ? nodeData.key : null)}
                            >
                                <span
                                    className={styles.nodeActionBtn}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    <MoreOutlined />
                                </span>
                            </Dropdown>
                        ) : (
                            <span className={styles.nodeCount}>{nodeData.count}</span>
                        )
                    )}
                </div>
            </div>
        );
    };

    const refreshContentList = () => {
        const folderId = activeMenu === 'refImage_all' ? '' : (activeMenu || '');
        fetchMaterialList({
            scope: activeTab,
            folderId,
            keyword: searchKeyword,
            pageIndex: 1,
        });
    };

    return (
        <div className={styles.Sidebar}>
            {/* 顶部 Tab 切换 */}
            <div className={styles.tabHeader}>
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    items={[
                        { key: 'personal', label: '个人素材' },
                        { key: 'enterprise', label: '企业素材' }
                    ]}
                />
            </div>

            {/* 菜单列表 */}
            <div className={`${styles.menuList} _beauty_scrollbar`}>
                {/* 参考图管理（固定头部） */}
                <div style={{ paddingBottom: 8, width: '100%', display: 'flex' }}>
                    <div className={styles.treeNodeContent} style={{ flex: 1 }}>
                        <span className={styles.nodeIcon}><Iconfont style={{ fontSize: 18 }} type="icon-Material-ReferenceStandardOutlined" /></span>
                        <span className={styles.nodeTitle}>参考图管理</span>
                        <span className={styles.nodeCount}>{menuTotal}</span>
                    </div>
                </div>
                {/* 全部参考图 */}
                <div
                    style={{ paddingBottom: 8, width: '100%', display: 'flex', cursor: 'pointer' }}
                    onClick={() => { setActiveMenu('refImage_all'); setActiveMenuName('全部参考图'); }}
                >
                    <div className={`${styles.treeNodeContent} ${activeMenu === 'refImage_all' ? styles.activeNode : ''}`} style={{ flex: 1 }}>
                        <span className={styles.switcherPlaceholder} />
                        <span className={styles.nodeIcon}>
                            {activeMenu === 'refImage_all' ? <Iconfont type="icon-Material-FolderOpenOutlined" /> : <Iconfont type="icon-Material-FolderOutlined" />}
                        </span>
                        <span className={styles.nodeTitle}>全部参考图</span>
                        <span className={styles.nodeCount}>{menuTotal}</span>
                    </div>
                </div>
                {/* 接口返回的文件夹树 */}
                {menuLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
                        <Spin size="small" />
                    </div>
                ) : (
                    <Tree
                        treeData={treeData}
                        expandedKeys={expandedKeys}
                        onExpand={handleExpand}
                        selectedKeys={[activeMenu]}
                        onSelect={handleSelect}
                        titleRender={titleRender}
                        blockNode
                        showIcon={false}
                        switcherIcon={null}
                        className={styles.customTree}
                    />
                )}
                <div className={styles.comingSoonSection}>
                    <div className={styles.comingSoonItem}>
                        <span className={styles.comingSoonLeft}>
                            <span className={styles.comingSoonIcon}><PictureOutlined /></span>
                            <span>模特图管理</span>
                        </span>
                        <span className={styles.comingSoonTag}>敬请期待</span>
                    </div>
                    <div className={styles.comingSoonItem}>
                        <span className={styles.comingSoonLeft}>
                            <span className={styles.comingSoonIcon}><VideoCameraOutlined /></span>
                            <span>参考视频管理</span>
                        </span>
                        <span className={styles.comingSoonTag}>敬请期待</span>
                    </div>
                </div>
            </div>

            <div className={styles.createFolderBtnWrap}>
                <Button
                    className={styles.createBtn}
                    onClick={() => {
                        setFolderModalMode('create');
                        setFolderModalParentId('');
                        setFolderModalInitialName('');
                        setFolderModalOpen(true);
                    }}>
                    <Iconfont style={{ fontSize: 16 }} type="icon-Material-NewFolderOutlined" />
                    新建文件夹
                </Button>
            </div>

            {/* 新建/重命名文件夹弹窗 */}
            <FolderNameModal
                open={folderModalOpen}
                mode={folderModalMode}
                initialName={folderModalInitialName}
                parentId={folderModalParentId}
                renameId={folderModalRenameId}
                scope={activeTab}
                onSuccess={() => {
                    setFolderModalOpen(false);
                    fetchFolderTree(activeTab);
                    refreshContentList();
                }}
                onCancel={() => setFolderModalOpen(false)}
            />

            {/* 删除文件夹弹窗 */}
            <DeleteFolderModal
                open={deleteModalOpen}
                folderId={deleteNodeId}
                onSuccess={() => {
                    setDeleteModalOpen(false);
                    const nextKey = deleteFallbackTarget?.key || 'refImage_all';
                    const nextName = deleteFallbackTarget?.name || '全部参考图';
                    setActiveMenu(nextKey);
                    setActiveMenuName(nextName);
                    fetchFolderTree(activeTab);
                    fetchMaterialList({
                        scope: activeTab,
                        folderId: nextKey === 'refImage_all' ? '' : nextKey,
                        keyword: searchKeyword,
                        pageIndex: 1,
                    });
                }}
                onCancel={() => setDeleteModalOpen(false)}
            />

            {/* 移动文件夹弹窗 */}
            <MoveFolderModal
                open={moveModalOpen}
                menuList={menuList}
                currentFolderId={moveNodeId}
                onSuccess={() => {
                    setMoveModalOpen(false);
                    fetchFolderTree(activeTab);
                    refreshContentList();
                }}
                onCancel={() => setMoveModalOpen(false)}
            />

            {/* 右键菜单 */}
            <TreeContextMenu
                visible={contextMenu.visible}
                position={contextMenu.position}
                nodeData={contextMenu.nodeData}
                onClose={handleContextMenuClose}
                onAction={handleContextMenuAction}
            />
        </div>
    );
};

export default Sidebar;
